import { deactivateAccount } from "api/auth";
import { useDialogFocus } from "hooks/useDialogFocus";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "Session";

import type { DeactivateAccountDialogProps } from "../settings.types";

import styles from "./DeactivateAccountDialog.module.css";

/**
 * Confirmation dialog for deactivating the account. Spells out exactly what happens (subscription
 * auto-renewal cancelled, all trees hidden, 6-month grace before permanent deletion) before the
 * destructive call; on success the user is signed out everywhere.
 */
const DeactivateAccountDialog = ({ onClose }: DeactivateAccountDialogProps) => {
	const { t } = useTranslation("settingsPage");
	const { getAccessToken, signOut } = useSession();
	const dialogRef = useDialogFocus<HTMLDivElement>();

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");

	const onConfirm = useCallback(async (): Promise<void> => {
		if (isSubmitting) {
			return;
		}

		setErrorMessage("");
		setIsSubmitting(true);
		try {
			const token = await getAccessToken();
			if (!token) {
				return;
			}

			await deactivateAccount(token);
			signOut();
		} catch (error) {
			setErrorMessage(error instanceof Error ? error.message : t("deactivate.genericError"));
			setIsSubmitting(false);
		}
	}, [getAccessToken, isSubmitting, signOut, t]);

	return (
		<div className={styles.overlay}>
			<div
				ref={dialogRef}
				aria-labelledby="deactivate-account-title"
				aria-modal="true"
				className={styles.dialog}
				role="alertdialog">
				<header className={styles.header}>
					<h2 id="deactivate-account-title">{t("deactivate.title")}</h2>
					<p>{t("deactivate.description")}</p>
				</header>

				<ul className={styles.consequences}>
					<li>{t("deactivate.consequenceSubscription")}</li>
					<li>{t("deactivate.consequenceTrees")}</li>
					<li>{t("deactivate.consequenceReactivate")}</li>
					<li className={styles.finalWarning}>{t("deactivate.consequenceDeletion")}</li>
				</ul>

				{errorMessage && <p className={styles.error}>{errorMessage}</p>}

				<footer className={styles.footer}>
					<button className={styles.cancelButton} disabled={isSubmitting} onClick={onClose} type="button">
						{t("deactivate.cancel")}
					</button>
					<button className={styles.dangerButton} disabled={isSubmitting} onClick={onConfirm} type="button">
						{isSubmitting ? t("deactivate.deactivating") : t("deactivate.confirm")}
					</button>
				</footer>
			</div>
		</div>
	);
};

export default DeactivateAccountDialog;
